import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MobileProctor() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading, ready, validating, recording, ended, error
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [chunkCount, setChunkCount] = useState(0);
  const [violations, setViolations] = useState([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const uploadIntervalRef = useRef(null);
  const captureIntervalRef = useRef(null);

  // Fetch session info on mount
  useEffect(() => {
    fetchSession();
    return () => cleanup();
  }, [token]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/proctoring/mobile/session/${token}`);
      if (!res.ok) throw new Error('Invalid or expired link');
      const data = await res.json();
      if (data.already_connected) {
        setError('This mobile camera is already connected');
        setPhase('error');
        return;
      }
      setSessionInfo(data);
      setPhase('ready');
    } catch (err) {
      setError(err.message || 'Failed to connect');
      setPhase('error');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Connect to session
      const formData = new FormData();
      formData.append('token', token);
      await fetch(`${API_URL}/api/proctoring/mobile/connect`, { method: 'POST', body: formData });

      setPhase('validating');
    } catch (err) {
      setError('Camera access denied. Please allow camera permission.');
      setPhase('error');
    }
  };

  const validatePlacement = async () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const b64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];

    try {
      setValidationResult({ checking: true });
      const formData = new FormData();
      formData.append('token', token);
      formData.append('image_base64', b64);
      const res = await fetch(`${API_URL}/api/proctoring/mobile/validate-placement`, {
        method: 'POST', body: formData
      });
      const data = await res.json();
      setValidationResult(data);

      if (data.valid) {
        startRecording();
      }
    } catch (err) {
      setValidationResult({ valid: false, suggestion: 'Validation failed. Try again.' });
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    try {
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8' : 'video/webm';
      const recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 250000 });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(5000);
      setIsRecording(true);
      setPhase('recording');

      // Upload chunks every 30s
      uploadIntervalRef.current = setInterval(() => uploadChunk(false), 30000);

      // Capture frames for AI analysis every 45s
      captureIntervalRef.current = setInterval(captureFrame, 45000);
    } catch (err) {
      setError('Failed to start recording');
    }
  };

  const uploadChunk = async (isFinal) => {
    if (chunksRef.current.length === 0) return;
    const chunks = [...chunksRef.current];
    chunksRef.current = [];
    const blob = new Blob(chunks, { type: recorderRef.current?.mimeType || 'video/webm' });
    if (blob.size < 3000) return;

    try {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const b64 = reader.result.split(',')[1];
        const formData = new FormData();
        formData.append('token', token);
        formData.append('video_base64', b64);
        formData.append('chunk_index', chunkIndexRef.current);
        formData.append('is_final', isFinal);
        formData.append('mime_type', recorderRef.current?.mimeType || 'video/webm');
        chunkIndexRef.current += 1;
        setChunkCount(chunkIndexRef.current);
        await fetch(`${API_URL}/api/proctoring/mobile/upload-chunk`, { method: 'POST', body: formData });
      };
    } catch (err) {
      console.error('Chunk upload error:', err);
    }
  };

  const captureFrame = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const b64 = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];

    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('image_base64', b64);
      const res = await fetch(`${API_URL}/api/proctoring/mobile/capture-frame`, {
        method: 'POST', body: formData
      });
      const data = await res.json();
      if (data.violations && data.violations.length > 0) {
        setViolations(prev => [...prev, ...data.violations]);
      }
    } catch (err) {
      console.error('Frame capture error:', err);
    }
  };

  const cleanup = () => {
    if (uploadIntervalRef.current) clearInterval(uploadIntervalRef.current);
    if (captureIntervalRef.current) clearInterval(captureIntervalRef.current);
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
      uploadChunk(true);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    const formData = new FormData();
    formData.append('token', token);
    fetch(`${API_URL}/api/proctoring/mobile/disconnect`, { method: 'POST', body: formData }).catch(() => {});
  };

  const endSession = () => {
    cleanup();
    setPhase('ended');
    setIsRecording(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col" data-testid="mobile-proctor-page">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-blue-400">HIMT Mobile Proctor</h1>
          {sessionInfo && (
            <p className="text-xs text-gray-400">{sessionInfo.student_name} — {sessionInfo.test_name}</p>
          )}
        </div>
        {isRecording && (
          <div className="flex items-center gap-2 animate-pulse">
            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
            <span className="text-xs font-semibold text-red-400">REC</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">

        {/* Loading */}
        {phase === 'loading' && (
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Connecting to test session...</p>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-red-400 mb-2">Connection Error</h2>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        )}

        {/* Ready — show instructions and start button */}
        {phase === 'ready' && (
          <div className="text-center max-w-sm space-y-6">
            <div className="w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold">Mobile Camera Setup</h2>
            <p className="text-sm text-gray-400">{sessionInfo?.test_name}</p>
            <div className="bg-gray-900 rounded-xl p-4 text-left space-y-2 text-sm text-gray-300">
              <p className="font-semibold text-white mb-2">Position your phone so it shows:</p>
              <div className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">1.</span>
                <span>Your laptop/desktop screen</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">2.</span>
                <span>You sitting at your desk</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">3.</span>
                <span>The surrounding area behind you</span>
              </div>
              <p className="text-yellow-400 text-xs mt-3">Use the rear camera for best results. Keep the phone stable (use a stand).</p>
            </div>
            <button
              onClick={startCamera}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-lg transition-colors"
              data-testid="mobile-start-camera-btn"
            >
              Start Camera
            </button>
          </div>
        )}

        {/* Validating — show camera feed + validate button */}
        {phase === 'validating' && (
          <div className="w-full max-w-sm space-y-4">
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute top-2 left-2 bg-green-500/80 text-white text-xs px-2 py-0.5 rounded-full">
                Camera Active
              </div>
            </div>

            {validationResult?.checking && (
              <div className="text-center py-4">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-gray-400">AI is checking camera placement...</p>
              </div>
            )}

            {validationResult && !validationResult.checking && !validationResult.valid && (
              <div className="bg-red-900/30 border border-red-800 rounded-xl p-3 text-sm">
                <p className="font-semibold text-red-400 mb-1">Placement not valid</p>
                <p className="text-gray-300">{validationResult.suggestion || validationResult.description}</p>
              </div>
            )}

            {validationResult?.valid && (
              <div className="bg-green-900/30 border border-green-800 rounded-xl p-3 text-sm text-center">
                <p className="font-semibold text-green-400">Placement validated! Starting recording...</p>
              </div>
            )}

            {(!validationResult || (!validationResult.checking && !validationResult.valid)) && (
              <button
                onClick={validatePlacement}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors"
                data-testid="mobile-validate-btn"
              >
                Validate Camera Placement
              </button>
            )}
          </div>
        )}

        {/* Recording — show camera feed + status */}
        {phase === 'recording' && (
          <div className="w-full max-w-sm space-y-4">
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute top-2 left-2 flex items-center gap-2 bg-red-600/90 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full"></span>
                Recording
              </div>
              <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                Chunks: {chunkCount}
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Status</span>
                <span className="text-green-400 font-medium">Recording</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Chunks uploaded</span>
                <span className="text-white font-medium">{chunkCount}</span>
              </div>
              {violations.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Violations</span>
                  <span className="text-red-400 font-medium">{violations.length}</span>
                </div>
              )}
            </div>

            <div className="bg-yellow-900/30 border border-yellow-800 rounded-xl p-3 text-xs text-yellow-200 text-center">
              Do NOT close this page or switch apps. Keep the phone in position until the test ends.
            </div>

            <button
              onClick={endSession}
              className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 py-2 rounded-xl text-sm transition-colors"
              data-testid="mobile-end-btn"
            >
              End Mobile Monitoring
            </button>
          </div>
        )}

        {/* Ended */}
        {phase === 'ended' && (
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-green-400 mb-2">Session Complete</h2>
            <p className="text-gray-400 text-sm">Mobile monitoring has ended. You can close this page.</p>
            <p className="text-gray-500 text-xs mt-2">{chunkCount} video chunks uploaded</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-2 text-center text-xs text-gray-600">
        HIMT ExamPoint — Secure Proctoring System
      </div>
    </div>
  );
}

export default MobileProctor;
